// Turn bare URLs in notes into clickable links. Notes come either as legacy
// plain text or as Quill-produced HTML; both paths run through here.

// Global matcher used for splitting. Kept local to each call site (matchAll
// creates its own iterator) so the shared lastIndex is never a problem.
const URL_RE = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
// Cheap, stateless guard for "is there possibly a URL in here".
const HAS_URL = /(https?:\/\/|www\.)/i;
// Trailing characters that are almost never part of the URL itself.
const TRAILING = /[.,;:!?)\]}'"»]+$/;

export interface TextSegment {
  text: string;
  href: string | null;
}

/** Split plain text into link / non-link segments, trimming trailing punctuation. */
export function splitTextByUrl(text: string): TextSegment[] {
  if (!HAS_URL.test(text)) return [{ text, href: null }];
  const out: TextSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    let raw = m[0];
    let trail = "";
    const t = raw.match(TRAILING);
    if (t) {
      trail = t[0];
      raw = raw.slice(0, raw.length - trail.length);
    }
    if (!raw) continue;
    if (start > last) out.push({ text: text.slice(last, start), href: null });
    out.push({ text: raw, href: raw.startsWith("www.") ? `https://${raw}` : raw });
    if (trail) out.push({ text: trail, href: null });
    last = start + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), href: null });
  return out.length > 0 ? out : [{ text, href: null }];
}

/**
 * Autolink bare URLs inside an HTML string, leaving existing <a> tags alone,
 * and force every link to open safely in a new tab. Runs in the browser only
 * (this app has no SSR), using the DOM to avoid mangling markup.
 */
export function linkifyHtml(html: string): string {
  const container = document.createElement("div");
  container.innerHTML = html;
  linkifyWithin(container);
  container.querySelectorAll("a").forEach((a) => {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer nofollow");
  });
  return container.innerHTML;
}

function linkifyWithin(node: Node): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent ?? "";
      if (!HAS_URL.test(text)) continue;
      const segments = splitTextByUrl(text);
      if (!segments.some((s) => s.href)) continue;
      const frag = document.createDocumentFragment();
      for (const seg of segments) {
        if (seg.href) {
          const a = document.createElement("a");
          a.href = seg.href;
          a.textContent = seg.text;
          frag.appendChild(a);
        } else {
          frag.appendChild(document.createTextNode(seg.text));
        }
      }
      child.parentNode?.replaceChild(frag, child);
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      // Leave content of existing anchors untouched.
      if ((child as Element).tagName === "A") continue;
      linkifyWithin(child);
    }
  }
}
